import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { Box, Typography, Stack, Link, styled } from '@mui/material';
import { theme } from '~/shared/styles/theme';
import {
  LocalStorageKeys,
  ROUTES,
  SENTRY_OPERATIONS,
} from '~/shared/constants';
import { EXTERNAL_URLS } from 'common';
import { handleLogout } from '~/shared/utils/auth';
import {
  ConsentCheckbox,
  PrimaryButton,
  OnboardingLayout,
} from '../components';
import { PROVISION_USER, RESPOND_TO_INVITATION } from '~/graphql/mutations';
import { GET_ONBOARDING_STATUS, ME } from '~/graphql/queries';
import { useAuth0 } from '@auth0/auth0-react';
import introGraphic from '~/assets/intro-graphic.png';
import { Lock } from '~/assets/icons';
import useToast from '~/shared/hooks/useToast';
import Toast from '~/shared/components/Toast';
import { InvitationResponse } from '~/graphql/gen/graphql';
import { reportError } from '~/shared/utils/sentry';

interface ConsentFormData {
  consent: boolean;
}

export const ConsentScreen = () => {
  const navigate = useNavigate();
  const { logout } = useAuth0();
  const { toast, showToast, hideToast } = useToast();

  const handleLogOut = () => {
    handleLogout(logout);
  };

  const { data: onboardingData, loading: queryLoading } = useQuery(
    GET_ONBOARDING_STATUS,
  );

  const { control, watch, handleSubmit } = useForm<ConsentFormData>({
    defaultValues: {
      consent: onboardingData?.onboardingStatus?.consentAcknowledged ?? false,
    },
  });

  const [provisionUser, { loading: mutationLoading }] = useMutation(
    PROVISION_USER,
    {
      refetchQueries: [{ query: GET_ONBOARDING_STATUS }, { query: ME }],
      awaitRefetchQueries: true,
    },
  );

  const [respondToInvitation] = useMutation(RESPOND_TO_INVITATION, {
    refetchQueries: [{ query: ME }],
    awaitRefetchQueries: true,
  });

  const consentChecked = watch('consent');

  const onSubmit = async () => {
    if (!consentChecked) {
      showToast('Please accept the terms and conditions to continue.', 'error');

      return;
    }

    try {
      await provisionUser();

      const invitationToken = localStorage.getItem(
        LocalStorageKeys.InvitationToken,
      );
      const invitationDecision = localStorage.getItem(
        LocalStorageKeys.InvitationDecision,
      );

      // Only call respondToInvitation for accepts - declines are handled
      // in createProgrammaticUser on the backend
      if (
        invitationToken &&
        invitationDecision === InvitationResponse.Accept.toLowerCase()
      ) {
        try {
          // Call respondToInvitation to link user to the inviter's tree
          await respondToInvitation({
            variables: {
              input: {
                token: invitationToken,
                response: InvitationResponse.Accept,
              },
            },
          });
        } catch (invitationError) {
          // Log error but don't prevent navigation - user is already provisioned
          reportError(invitationError, {
            operation: SENTRY_OPERATIONS.INVITATION_RESPONSE,
          });
        }
      }

      // Clear invitation data from localStorage regardless of path
      // to prevent stale data from affecting future flows
      localStorage.removeItem(LocalStorageKeys.InvitationToken);
      localStorage.removeItem(LocalStorageKeys.InvitationDecision);

      navigate(ROUTES.ONBOARDING.CONSENT);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';

      showToast(
        `Unable to create your account. Please try again. ${errorMessage}`,
        'error',
      );
    }
  };

  if (queryLoading) {
    return null; // TODO: replace with loading spinner component
  }

  // Force form remount when data loads to re-evaluate defaultValues with
  // fetched consent status
  const formKey =
    onboardingData?.onboardingStatus?.consentAcknowledged !== undefined
      ? 'with-data'
      : 'loading';

  return (
    <OnboardingLayout showBack={true} onBack={handleLogOut}>
      <HeroContainer>
        <img src={introGraphic} alt="Intro Graphic" width={228} height={168} />
      </HeroContainer>

      <Headline>Uncover Genetic Risks</Headline>

      <Stack alignItems="center">
        <DescriptionText>
          Quoiden helps families discover OTC deficiency risk by combining
          family trees with expert-backed free genetic testing. Your family
          history could hold the key to life-saving insights.
        </DescriptionText>

        <PrivacyContainer>
          <Lock />

          <PrivacyText>
            Everything you share is private, secure, and fully consent-driven.
          </PrivacyText>
        </PrivacyContainer>
      </Stack>

      <Box key={formKey}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ marginBottom: theme.spacing(7.5) }}>
            <ConsentCheckbox
              name="consent"
              control={control}
              label={
                <Typography
                  fontSize="14px"
                  lineHeight="20px"
                  fontWeight={400}
                  color={theme.palette.custom.mediumBrown}
                >
                  By selecting this checkbox, I agree to Quoiden's{' '}
                  <StyledLink
                    href={EXTERNAL_URLS.TERMS_OF_SERVICE}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="always"
                  >
                    Terms of Service
                  </StyledLink>
                  ,{' '}
                  <StyledLink
                    href={EXTERNAL_URLS.PRIVACY_STATEMENT}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="always"
                  >
                    Privacy Statement
                  </StyledLink>
                  , and{' '}
                  <StyledLink
                    href={EXTERNAL_URLS.COOKIE_POLICY}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="always"
                  >
                    Cookie Policy
                  </StyledLink>
                  .
                </Typography>
              }
            />
          </Box>

          <PrimaryButton
            type="submit"
            variant="brown"
            disabled={!consentChecked || queryLoading || mutationLoading}
          >
            {mutationLoading ? 'Creating Account...' : 'Create Account'}
          </PrimaryButton>
        </form>
      </Box>

      <Toast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={hideToast}
      />
    </OnboardingLayout>
  );
};

const HeroContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: theme.spacing(4),
});

const Headline = styled(Typography)({
  fontSize: '45px',
  fontWeight: 500,
  color: theme.palette.custom.darkBrown,
  textAlign: 'center',
  marginBottom: theme.spacing(2),
});

const DescriptionText = styled(Typography)({
  fontSize: '16px',
  fontWeight: 600,
  maxWidth: '550px',
  color: theme.palette.custom.mediumBrown,
  textAlign: 'center',
  marginBottom: theme.spacing(6),
  lineHeight: 1.6,
});

const PrivacyContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(9),
});

const PrivacyText = styled(Typography)({
  fontSize: '15px',
  fontWeight: 500,
  color: theme.palette.custom.mediumBrown,
  lineHeight: '21px',
});

const StyledLink = styled(Link)({
  color: theme.palette.custom.darkBrown,
  textDecoration: 'underline',
  cursor: 'pointer',
  fontWeight: 800,
});
